import { Alert, Button, Card, Checkbox, Form, Input, Popconfirm, Space, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Pagination as ZhPagination, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { useUserStore } from "@/views/stores";
import type { GroupCompanyOption, SupplierPageResult, SupplierQuery, SupplierRow } from "./types";

const defaultQuery: SupplierQuery = { page: 1, size: 20 };
const supplierTypeLabels: Record<number, string> = { 1: "企业供应商", 2: "个人供应商" };
const showLocationLabels: Record<number, string> = { 1: "首页", 2: "供应商列表" };

/** 按旧 Vue 列表的 DATETIME_PICKER_FORMAT 展示供应商创建时间。 */
function formatSupplierCreatedTime(value?: string): string {
    return dateFormat(value, DATETIME_PICKER_FORMAT);
}

type SupplierFilterValues = {
    supplierName?: string;
    type?: number[];
    groupCompanyId?: number;
};

export interface SupplierPageProps {
    deleteSupplier?: (id: number | string) => Promise<void>;
    loadGroupCompanies?: (name: string) => Promise<GroupCompanyOption[]>;
    loadSuppliers?: (query: SupplierQuery) => Promise<SupplierPageResult>;
    permissions?: string[];
}

/** 复用 Vue 供应商分页接口。 */
export async function loadSuppliers(query: SupplierQuery): Promise<SupplierPageResult> {
    const response = await request.service.get<SupplierPageResult>("/admin/supplier-admin/paging", { params: query });
    return response.data;
}

/** 复用 Vue 供应商删除接口，保留后端路径而非通用 REST 路径。 */
export async function deleteSupplier(id: number | string): Promise<void> {
    await request.service.delete(`/admin/supplier-admin/delete/${id}`);
}

/** 复用 Vue 集团远程选择接口，搜索词仍使用旧契约的 name 参数。 */
export async function loadGroupCompanies(name: string): Promise<GroupCompanyOption[]> {
    const response = await request.service.get<GroupCompanyOption[]>("/admin/group-company/selector", {
        params: { name }
    });
    return response.data;
}

/** 将 Vue 供应商分页返回的类型和展示位置枚举统一为列表文案使用的数字。 */
function normalizeSupplierRows(rows?: SupplierRow[]): SupplierRow[] {
    return (rows || []).map(row => ({
        ...row,
        supplier: row.supplier
            ? {
                  ...row.supplier,
                  type: row.supplier.type == null ? undefined : Number(row.supplier.type),
                  showLocation: row.supplier.showLocation == null ? undefined : Number(row.supplier.showLocation)
              }
            : undefined
    }));
}

/** 供应商管理列表，迁移 Vue 首屏字段和核心供应商操作。 */
export function SupplierPage({
    deleteSupplier: remove = deleteSupplier,
    loadGroupCompanies: loadGroups = loadGroupCompanies,
    loadSuppliers: load = loadSuppliers,
    permissions
}: SupplierPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeButtonBanned = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const canWrite = currentPermissions.includes("ROLE_SUPPLIER_DEAL");
    // 沿用旧 Vue 的全局按钮禁用契约；注入 permissions 时同步支持隔离测试和页面预览。
    const buttonBanned = permissions ? permissions.includes("ROLE_BUTTON_BAN") : storeButtonBanned;
    const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [groupCompanySearch, setGroupCompanySearch] = useState("");
    const [groupCompanyOptions, setGroupCompanyOptions] = useState<GroupCompanyOption[]>([]);
    const [query, setQuery] = useState<SupplierQuery>(defaultQuery);
    const [filterForm] = Form.useForm<SupplierFilterValues>();

    const refresh = async (targetQuery: SupplierQuery = query) => {
        const result = await load(targetQuery);
        setSuppliers(normalizeSupplierRows(result.list));
        setTotal(result.total || 0);
    };

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setSuppliers(normalizeSupplierRows(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("供应商加载失败");
                    setSuppliers([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    const handleDelete = async (id?: number | string) => {
        if (id == null) return;
        try {
            // 删除或刷新失败时保留当前供应商列表，并展示可恢复的 Vue 兼容错误提示。
            await remove(id);
            await refresh();
            setErrorMessage(null);
        } catch {
            setErrorMessage("供应商删除失败");
        }
    };

    // 将旧 Vue 供应商名称筛选转换为供应商分页接口参数。
    const submitFilters = ({ groupCompanyId, supplierName, type }: SupplierFilterValues) => {
        setErrorMessage(null);
        setQuery({
            ...defaultQuery,
            ...(supplierName ? { supplierName } : {}),
            ...(type?.length ? { type: type.map(Number) } : {}),
            ...(groupCompanyId ? { groupCompanyId: Number(groupCompanyId) } : {})
        });
    };

    // 清空筛选项和远程候选项，恢复旧 Vue 首屏分页参数。
    const resetFilters = () => {
        filterForm.resetFields();
        setGroupCompanySearch("");
        setGroupCompanyOptions([]);
        setQuery(defaultQuery);
    };

    // 仅在用户输入集团关键词时请求候选项，保持旧 Vue remote select 的触发时机。
    const searchGroupCompanies = async (name: string) => {
        if (!name.trim()) {
            setGroupCompanyOptions([]);
            return;
        }
        const options = await loadGroups(name);
        setGroupCompanyOptions(options || []);
    };

    // 保留旧 Vue 供应商 ProTable 的创建时间排序参数，映射为分页接口 sortKey/sortOrder。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(currentQuery => {
                const nextQuery = { ...currentQuery, page: 1 };
                if (!order) {
                    nextQuery.sortKey = "";
                    nextQuery.sortOrder = "";
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 列契约承接旧 Vue 供应商 ProTable 的嵌套字段和操作插槽。
    const columns: TableColumnProps<SupplierRow>[] = [
        {
            key: "code",
            label: "序号",
            prop: "supplier",
            render: ({ row }) => (
                <Button
                    aria-label="查看供应商详情"
                    type="link"
                    onClick={() =>
                        void navigate(`/supplierManagement/enterpriseManage/detail?id=${row.supplier?.id || ""}`)
                    }
                >
                    {row.supplier?.code || "-"}
                </Button>
            )
        },
        {
            key: "supplierName",
            label: "供应商名称",
            prop: "supplier",
            render: ({ row }) => row.supplier?.supplierName || "-"
        },
        {
            key: "type",
            label: "供应商类型",
            prop: "supplier",
            render: ({ row }) => supplierTypeLabels[row.supplier?.type || 0] || "-"
        },
        {
            key: "groupCompanyName",
            label: "所属集团",
            prop: "supplier",
            render: ({ row }) => row.supplier?.groupCompanyName || "-"
        },
        {
            key: "legalRepresentative",
            label: "法人姓名",
            prop: "supplier",
            render: ({ row }) => row.supplier?.legalRepresentative || "-"
        },
        {
            key: "socialCreditCode",
            label: "统一社会信用代码",
            prop: "supplier",
            render: ({ row }) => row.supplier?.socialCreditCode || "-"
        },
        {
            key: "location",
            label: "所在地",
            prop: "supplier",
            render: ({ row }) => `${row.supplier?.province || "-"}${row.supplier?.city || "-"}`
        },
        {
            key: "showLocation",
            label: "展示位置",
            prop: "supplier",
            render: ({ row }) => showLocationLabels[row.supplier?.showLocation || 0] || "-"
        },
        {
            key: "createdTime",
            label: "创建时间",
            prop: "createdTime",
            sortable: true,
            render: ({ row }) => formatSupplierCreatedTime(row.supplier?.createdTime)
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            fixed: "right",
            render: ({ row }) => {
                if (!canWrite) return null;
                const id = row.supplier?.id;
                return (
                    <Space>
                        <Button
                            aria-label="编辑供应商"
                            disabled={buttonBanned}
                            size="small"
                            onClick={() => void navigate(`/supplierManagement/enterpriseManage/edit?id=${id || ""}`)}
                        >
                            修改
                        </Button>
                        {!row.productConsignment?.length ? (
                            <Popconfirm
                                cancelText="取消"
                                okText="确定"
                                title="确认删除该供应商吗？"
                                onConfirm={() => void handleDelete(id)}
                            >
                                <Button aria-label="删除供应商" danger disabled={buttonBanned} size="small">
                                    删除
                                </Button>
                            </Popconfirm>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="supplier-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<SupplierRow>
                    data={suppliers}
                    columns={columns}
                    emptyText="暂无供应商"
                    pagination={false}
                    rowKey={record => String(record.supplier?.id || record.supplier?.code)}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                供应商管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                <Form.Item label="供应商名称" name="supplierName">
                                    <Input placeholder="请输入供应商名称搜索" />
                                </Form.Item>
                                <Form.Item label="供应商类型" name="type">
                                    <Checkbox.Group
                                        options={[
                                            { label: "企业供应商", value: 1 },
                                            { label: "个人供应商", value: 2 }
                                        ]}
                                    />
                                </Form.Item>
                                <Form.Item label="所属集团">
                                    <Space>
                                        <Input
                                            value={groupCompanySearch}
                                            placeholder="请输入关键字选择集团搜索"
                                            onChange={event => {
                                                setGroupCompanySearch(event.target.value);
                                                void searchGroupCompanies(event.target.value);
                                            }}
                                        />
                                        <Form.Item noStyle name="groupCompanyId">
                                            <select aria-label="所属集团">
                                                <option value="">请选择集团</option>
                                                {groupCompanyOptions.map(option => (
                                                    <option key={option.id} value={String(option.id)}>
                                                        {option.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </Form.Item>
                                    </Space>
                                </Form.Item>
                                <Space>
                                    <Button htmlType="submit" type="primary">
                                        查询供应商
                                    </Button>
                                    <Button onClick={resetFilters}>重置</Button>
                                </Space>
                            </Form>

                            {canWrite ? (
                                <div className="mb-4">
                                    <Button
                                        disabled={buttonBanned}
                                        type="primary"
                                        onClick={() => void navigate("/supplierManagement/enterpriseManage/add")}
                                    >
                                        新增供应商
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                total={total}
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default SupplierPage;
